import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, readApiError } from '../../lib/api';
import { hasPermission, useMe } from '../../hooks/useAuth';

interface CustomerRow {
  id: number;
  idDocument: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string | null;
  city: string | null;
  notes: string | null;
}

interface CustomerForm {
  idDocument: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  notes?: string;
}

export function CustomersPage() {
  const me = useMe();
  const canEdit = hasPermission(me.data, 'os.create');
  const qc = useQueryClient();

  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CustomerRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['customers', q],
    queryFn: async () => {
      const { data } = await api.get<{ data: CustomerRow[] }>('/api/customers', {
        params: { q: q || undefined, limit: 100 },
      });
      return data.data;
    },
  });

  const createMut = useMutation({
    mutationFn: async (body: CustomerForm) => {
      await api.post('/api/customers', body);
    },
    onSuccess: () => {
      setCreating(false);
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const updateMut = useMutation({
    mutationFn: async (vars: { id: number; body: Partial<CustomerForm> }) => {
      await api.patch(`/api/customers/${vars.id}`, vars.body);
    },
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>Clientes</h1>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + Nuevo cliente
          </button>
        )}
      </div>

      <input
        className="input"
        placeholder="Buscar por cédula, nombre, email, teléfono..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ maxWidth: 360, marginBottom: '1rem' }}
      />

      {error && <p className="error">{error}</p>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
              <th style={{ padding: '0.75rem 1rem' }}>Cédula</th>
              <th style={{ padding: '0.75rem 1rem' }}>Nombre</th>
              <th style={{ padding: '0.75rem 1rem' }}>Contacto</th>
              <th style={{ padding: '0.75rem 1rem' }}>Ciudad</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data?.map((c) => (
              <tr key={c.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.75rem 1rem' }}><code>{c.idDocument}</code></td>
                <td style={{ padding: '0.75rem 1rem' }}>
                  <Link to="/customers/$id" params={{ id: String(c.id) }}>
                    {c.firstName} {c.lastName}
                  </Link>
                </td>
                <td style={{ padding: '0.75rem 1rem' }}>
                  {c.phone}<br />
                  <small style={{ color: 'var(--color-muted)' }}>{c.email}</small>
                </td>
                <td style={{ padding: '0.75rem 1rem' }}>{c.city ?? '—'}</td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                  {canEdit && (
                    <button className="btn" onClick={() => setEditing(c)}>Editar</button>
                  )}
                </td>
              </tr>
            ))}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
                  Sin clientes que coincidan.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && (
        <CustomerFormModal
          onCancel={() => setCreating(false)}
          onSubmit={(b) => createMut.mutate(b)}
          submitting={createMut.isPending}
        />
      )}
      {editing && (
        <CustomerFormModal
          initial={editing}
          onCancel={() => setEditing(null)}
          onSubmit={(b) => updateMut.mutate({ id: editing.id, body: b })}
          submitting={updateMut.isPending}
        />
      )}
    </section>
  );
}

function CustomerFormModal(props: {
  initial?: CustomerRow;
  onCancel: () => void;
  onSubmit: (body: CustomerForm) => void;
  submitting: boolean;
}) {
  const [form, setForm] = useState<CustomerForm>({
    idDocument: props.initial?.idDocument ?? '',
    firstName: props.initial?.firstName ?? '',
    lastName: props.initial?.lastName ?? '',
    email: props.initial?.email ?? '',
    phone: props.initial?.phone ?? '',
    address: props.initial?.address ?? '',
    city: props.initial?.city ?? '',
    notes: props.initial?.notes ?? '',
  });
  const isEdit = !!props.initial;

  const set = <K extends keyof CustomerForm>(k: K, v: CustomerForm[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  return (
    <div style={modalBackdrop}>
      <form
        className="card"
        style={{ width: 520, maxHeight: '85vh', overflowY: 'auto' }}
        onSubmit={(e) => {
          e.preventDefault();
          // omit idDocument on update (backend rejects it on PATCH)
          const body = isEdit ? { ...form, idDocument: undefined } as Partial<CustomerForm> : form;
          props.onSubmit(body as CustomerForm);
        }}
      >
        <h3 style={{ marginTop: 0 }}>{isEdit ? 'Editar cliente' : 'Nuevo cliente'}</h3>

        <div className="field">
          <label>Cédula / NIT</label>
          <input
            className="input"
            value={form.idDocument}
            onChange={(e) => set('idDocument', e.target.value)}
            disabled={isEdit}
            required
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>Nombre</label>
            <input className="input" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} required />
          </div>
          <div className="field">
            <label>Apellido</label>
            <input className="input" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} required />
          </div>
        </div>
        <div className="field">
          <label>Email</label>
          <input className="input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required />
        </div>
        <div className="field">
          <label>Teléfono</label>
          <input className="input" value={form.phone} onChange={(e) => set('phone', e.target.value)} required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>Dirección</label>
            <input className="input" value={form.address} onChange={(e) => set('address', e.target.value)} />
          </div>
          <div className="field">
            <label>Ciudad</label>
            <input className="input" value={form.city} onChange={(e) => set('city', e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Notas</label>
          <textarea className="input" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" disabled={props.submitting}>
            {props.submitting ? 'Guardando...' : isEdit ? 'Guardar' : 'Crear'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 100,
};
